<div class="debug-view-wrapper d-flex flex-column h-100 bg-body">

  <!-- Header -->
  <div
    class="debug-view-header d-flex align-items-center justify-content-between px-3 py-2 border-bottom flex-shrink-0">
    <div class="d-flex align-items-center gap-2">
      <i class="ri-bug-line fs-5"></i>
      <span
        class="fw-semibold"
        i18n="@@debug-view-title"
      >Debugging</span>
      <code class="text-muted small">{{ botId }}</code>
      <p-tag
        [severity]="connected ? 'success' : 'warn'"
        [value]="connected ? connectedLabel : connectingLabel"
      />
    </div>
    <div class="d-flex gap-2">
      <p-button
        size="small"
        [severity]="autoScroll ? 'primary' : 'secondary'"
        icon="ri-arrow-down-line"
        [pTooltip]="autoScrollLabel"
        i18n-pTooltip="@@debug-view-auto-scroll"
        tooltipPosition="bottom"
        (onClick)="toggleAutoScroll()"
      />
      <p-button
        size="small"
        severity="secondary"
        icon="ri-delete-bin-line"
        [pTooltip]="clearLabel"
        i18n-pTooltip="@@debug-view-clear"
        tooltipPosition="bottom"
        (onClick)="clearLog()"
      />
    </div>
  </div>

  <!-- View-Mode Umschalter -->
  <div class="debug-view-mode-switch d-flex align-items-center gap-2 px-3 py-2 border-bottom flex-shrink-0">
    <p-button
      size="small"
      [severity]="viewMode === 'flow' ? 'success' : 'secondary'"
      icon="ri-flashlight-line"
      [label]="flowModeLabel"
      (onClick)="setViewMode('flow')"
    />
    <p-button
      size="small"
      [severity]="viewMode === 'detail' ? 'success' : 'secondary'"
      icon="ri-table-line"
      [label]="detailModeLabel"
      (onClick)="setViewMode('detail')"
    />
  </div>

  <!-- Freitext-Filter (nur im Detail-Mode) -->
  @if (viewMode === 'detail') {
  <div class="debug-view-filter d-flex align-items-center gap-2 px-3 py-2 border-bottom flex-shrink-0">
    <i class="ri-filter-3-line text-muted"></i>
    <input
      type="text"
      class="form-control form-control-sm"
      style="max-width: 320px"
      [(ngModel)]="detailFilterText"
      [placeholder]="detailFilterPlaceholder"
    />
    <span class="text-muted small">{{ detailFilteredLog.length }} / {{ debugLog.length }}</span>
  </div>
  }

  <ng-template #apiRequestTpl let-entry="entry">
    @if (entry.apiRequestId) {
    <details class="mt-1" (toggle)="onApiRequestToggle($event, entry.apiRequestId)">
      <summary class="text-muted small" i18n="@@debug-api-request-show">API-Request anzeigen</summary>
      <div class="small text-muted mt-1" i18n="@@debug-api-request-retention">48 Stunden verfügbar. Zugangsdaten werden ausgeblendet.</div>
      @if (apiRequests()[entry.apiRequestId]; as request) {
        @switch (request.status) {
          @case ('loading') {
            <div class="small mt-1" i18n="@@debug-api-request-loading">API-Request wird geladen…</div>
          }
          @case ('ok') {
            <p-button
              class="d-block mt-1"
              size="small"
              severity="secondary"
              [text]="true"
              icon="ri-file-copy-line"
              label="API-Request kopieren"
              i18n-label="@@debug-api-request-copy"
              (onClick)="copyApiRequest(entry.apiRequestId)"
            />
            <pre class="small mt-1 mb-0 overflow-auto debug-explanation-pre">{{ request.requestText }}</pre>
          }
          @case ('unavailable') {
            <div class="small mt-1" i18n="@@debug-api-request-unavailable">Dieser API-Request ist nicht mehr verfügbar. Sende eine neue Nachricht an den Bot, um einen aktuellen Request zu sehen.</div>
          }
          @case ('error') {
            <div class="small text-danger mt-1" i18n="@@debug-api-request-load-error">Der API-Request konnte nicht geladen werden. Schließe die Ansicht und öffne sie erneut.</div>
          }
        }
      }
    </details>
    }
  </ng-template>

  <!-- Log List (Flow-Mode) -->
  @if (viewMode === 'flow') {
  <div
    #logContainer
    class="debug-view-log flex-grow-1 overflow-auto p-2"
  >
    <ng-template
      #entryTpl
      let-entry="entry"
    >
      <div
        class="debug-log-entry mb-1 p-2 border rounded"
        [class.debug-log-entry-ragai-event]="entry.action === 'ragai_event'"
        [class.debug-log-entry-historical]="entry.isHistorical"
      >
        @if (isRequestStartWithText(entry)) {
        <div
          class="fw-semibold small mb-1 text-truncate"
          [innerHTML]="entry.reason"
        ></div>
        }
        <div class="d-flex align-items-center gap-2 flex-wrap">
          @if (entry.action === 'ragai_event') {
          <i class="ri-flashlight-line debug-log-entry-ragai-event-icon"></i>
          }
          @if (entry.action !== 'ragai_event') {
          <p-tag
            [severity]="getActionSeverity(entry.action)"
            [value]="getActionLabel(entry.action)"
            [pTooltip]="entry.action"
            tooltipPosition="top"
            class="text-truncate"
            style="max-width: 220px"
          />
          }
          <span
            class="fw-semibold text-truncate flex-grow-1"
            [innerHTML]="getEntryTitle(entry)"
            [pTooltip]="entry.action === 'ragai_event' ? entry.title : undefined"
            tooltipPosition="top"
          ></span>
          @if (entry.executionDuration) {
          <span class="text-muted small flex-shrink-0">
            {{ formatDuration(entry.executionDuration) }}
          </span>
          }
          <span class="text-muted small font-monospace flex-shrink-0 ms-auto">
            {{ entry.created | date:'HH:mm:ss.SSS' }}
          </span>
        </div>
        @if (entry.reason && !isRequestStartWithText(entry)) {
        <div
          class="text-muted small mt-1 text-truncate"
          [innerHTML]="entry.reason"
        ></div>
        }
        @if (entry.explanation && entry.action !== 'ToolsAvailable') {
        <details class="mt-1">
          <summary class="text-muted small">{{ getExplanationLabel(entry) }}</summary>
          <pre class="small mt-1 mb-0 overflow-auto debug-explanation-pre">{{ formatExplanation(entry.explanation) }}
              </pre>
        </details>
        }
        <ng-container *ngTemplateOutlet="apiRequestTpl; context: { entry: entry }"></ng-container>
      </div>
    </ng-template>
    <ng-template
      #groupTpl
      let-group="group"
      let-noMargin="noMargin"
    >
      <!-- Gruppierter Verarbeitungsschritt: Nutzereingabe -> Tool-Aufrufe -> Bot-Antwort -->
      @if (group.requestId && group.entries.length > 1) {
      <div
        class="debug-log-group rounded"
        [class.mb-1]="!noMargin"
        [class.debug-log-entry-historical]="group.isHistorical"
      >
        <button
          type="button"
          class="debug-log-group-header d-flex align-items-center gap-2 flex-wrap w-100 border-0 bg-transparent p-2"
          (click)="toggleGroup(group.requestId)"
        >
          <i
            class="debug-log-group-icon flex-shrink-0"
            [class.ri-loop-right-line]="!isGroupComplete(group)"
            [class.debug-log-group-icon-spin]="!isGroupComplete(group)"
            [class.ri-arrow-down-s-line]="isGroupComplete(group) && !isGroupExpanded(group.requestId)"
            [class.ri-arrow-up-s-line]="isGroupComplete(group) && isGroupExpanded(group.requestId)"
            aria-hidden="true"
          ></i>
          <span
            class="fw-semibold small"
            i18n="@@debug-view-group-title"
          >Verarbeitung:</span>
          <p-tag
            severity="secondary"
            [value]="'Skills: ' + (getToolsAvailableCount(group) ?? 0)"
          />
          <p-tag
            severity="warn"
            [value]="'verwendet: ' + getToolCallCount(group)"
          />
          <p-tag
            severity="secondary"
            [value]="'Steps: ' + group.entries.length"
          />
          <span class="text-muted small font-monospace flex-shrink-0 ms-auto">
            {{ getGroupTimestamp(group) | date:'HH:mm:ss.SSS' }}
          </span>
        </button>
        @if (isGroupExpanded(group.requestId)) {
        <div class="debug-log-group-body px-2 pb-2">
          @for (entry of group.entries; track entry) {
          <ng-container *ngTemplateOutlet="entryTpl; context: { entry: entry }"></ng-container>
          }
        </div>
        }
      </div>
      } @else {
      @for (entry of group.entries; track entry) {
      <ng-container *ngTemplateOutlet="entryTpl; context: { entry: entry }"></ng-container>
      }
      }
      <!-- Einzelnes, nicht gruppiertes Ereignis -->
    </ng-template>
    @for (row of debugLogRows; track trackByRow(ri, row); let ri = $index) {
    @if (showSessionSeparatorForRow(ri)) {
    <div class="debug-log-session-separator d-flex align-items-center gap-2 my-2">
      <span class="debug-log-session-separator-line"></span>
      <span
        class="text-muted small text-nowrap"
        i18n="@@debug-view-session-start"
      >Aktuelle Session</span>
      <span class="debug-log-session-separator-line"></span>
    </div>
    }
    <!-- "Runde": Anfrage gestartet -> Verarbeitungsschritt(e) -> Antwort abgeschlossen,
        gemeinsam eingerahmt, da diese drei Teile immer zusammengehören. -->
    @if (row.kind === 'round') {
    <div
      class="debug-log-round rounded mb-1"
      [class.debug-log-entry-historical]="row.isHistorical"
    >
      @for (g of row.groups; track trackByGroup($index, g)) {
      <ng-container *ngTemplateOutlet="groupTpl; context: { group: g, noMargin: true }"></ng-container>
      }
    </div>
    } @else {
    <ng-container *ngTemplateOutlet="groupTpl; context: { group: row }"></ng-container>
    }
    }
    <!-- Empty State -->
    @if (filteredDebugLog.length === 0) {
    <div class="text-center text-muted py-5">
      <i class="ri-bug-line d-block fs-1 mb-2 opacity-25"></i>
      <span i18n="@@debug-view-empty">Noch keine Debug-Meldungen. Starte eine Unterhaltung mit dem Bot.</span>
    </div>
    }
  </div>
  }

  <!-- Log List (Detail-Mode): tabellarisch, ausnahmslos alle Events -->
  @if (viewMode === 'detail') {
  <div
    #logContainer
    class="debug-view-log flex-grow-1 overflow-auto p-2"
  >
    <table class="table table-sm table-hover debug-view-detail-table mb-0">
      <thead>
        <tr>
          <th
            class="text-nowrap"
            i18n="@@debug-view-col-origin"
          >Herkunft</th>
          <th i18n="@@debug-view-col-event">Event</th>
          <th i18n="@@debug-view-col-reason">Reason</th>
          <th
            class="text-nowrap"
            i18n="@@debug-view-col-duration"
          >Dauer</th>
        </tr>
      </thead>
      <tbody>
        @for (entry of detailFilteredLog; track trackByEntryRef($index, entry)) {
        <tr
          [class.debug-log-entry-historical]="entry.isHistorical"
          [class.debug-view-detail-row-frontend]="isFrontendEvent(entry)"
        >
          <td class="text-nowrap">
            <span
              class="d-inline-flex align-items-center gap-1 small"
              [class.text-info]="isFrontendEvent(entry)"
              [class.text-muted]="!isFrontendEvent(entry)"
            >
              <i [class]="isFrontendEvent(entry) ? 'ri-computer-line' : 'ri-server-line'"></i>
              {{ isFrontendEvent(entry) ? originFrontendLabel : originServerLabel }}
            </span>
          </td>
          <td>
            <p-tag
              [severity]="getActionSeverity(entry.action)"
              [value]="getEntrySearchKey(entry)"
              [pTooltip]="getEntryTitle(entry)"
              tooltipPosition="top"
              class="text-truncate"
              style="max-width: 280px"
            />
          </td>
          <td
            class="text-truncate small text-muted"
            style="max-width: 320px"
            [innerHTML]="entry.reason"
          ></td>
          <td class="text-muted small font-monospace text-nowrap">
            {{ entry.created | date:'HH:mm:ss.SSS' }}
            @if (entry.executionDuration) {
            <span>&nbsp;({{ formatDuration(entry.executionDuration) }})</span>
            }
          </td>
        </tr>
        @if (entry.apiRequestId) {
        <tr>
          <td colspan="4">
            <ng-container *ngTemplateOutlet="apiRequestTpl; context: { entry: entry }"></ng-container>
          </td>
        </tr>
        }
        }
      </tbody>
    </table>
    <!-- Empty State -->
    @if (detailFilteredLog.length === 0) {
    <div class="text-center text-muted py-5">
      <i class="ri-filter-off-line d-block fs-1 mb-2 opacity-25"></i>
      <span i18n="@@debug-view-detail-empty">Keine Ereignisse gefunden.</span>
    </div>
    }
  </div>
  }

</div>